import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { SESSION_EXPIRED, get, post } from './api.ts'
import type { SessionUser } from '../../shared/auth.ts'

/**
 * Who is signed in.
 *
 * `user === undefined` means "we have not asked yet" and is distinct from `null`, which
 * means "asked, nobody". Collapsing the two would bounce a signed-in user to the login
 * screen for a frame on every hard refresh.
 */
interface SessionState {
  user: SessionUser | null | undefined
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  setUser: (u: SessionUser) => void
}

const Ctx = createContext<SessionState>({
  user: undefined,
  refresh: async () => {},
  signOut: async () => {},
  setUser: () => {},
})

export function useSession(): SessionState {
  return useContext(Ctx)
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null | undefined>(undefined)

  const refresh = useCallback(async () => {
    try {
      const { user: next } = await get<{ user: SessionUser | null }>('/auth/me')
      setUserState(next)
    } catch {
      setUserState(null)
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await post('/auth/logout')
    } finally {
      setUserState(null)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Any 401 from a screen's own fetch means the session is gone — drop it here so the
  // guard sends them to sign-in, rather than leaving every card showing a load error.
  useEffect(() => {
    const onExpired = () => setUserState((current) => (current ? null : current))
    window.addEventListener(SESSION_EXPIRED, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired)
  }, [])

  const value = useMemo(
    () => ({ user, refresh, signOut, setUser: setUserState }),
    [user, refresh, signOut],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Wraps the trading app. Nothing behind it renders without a session. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useSession()
  const location = useLocation()

  if (user === undefined) {
    // Blank rather than a spinner: this resolves in one request and a flash of chrome
    // would be more distracting than a beat of nothing.
    return null
  }
  if (user === null) {
    // Remember where they were headed so signing in lands them there, not on Home.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  return <>{children}</>
}

/** Wraps the auth screens: a signed-in user has no business on the login page. */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { user } = useSession()
  const location = useLocation()

  if (user === undefined) return null
  if (user) {
    const next = new URLSearchParams(location.search).get('next')
    return <Navigate to={next && next.startsWith('/') ? next : '/'} replace />
  }
  return <>{children}</>
}
